import React, {useState, useCallback} from 'react';
import {observer} from 'mobx-react';

import {Dialog, DialogContent, IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import {useStores} from '../../../Hooks';
import {trainingAndFieldRoleEnum, contentCreatingRoleEnum, workspaceUsersEnum, sortingArrangeEnum} from '../../../serverClientShared/sharedEnums';
import StepIndicator from './StepIndicator';
import Step1Emails from './Step1Emails';
import Step2Permissions from './Step2Permissions';
import Step3FinalReview from './Step3FinalReview';
import useEmailInput from './useEmailInput';

const INITIAL_ROLES = {
    trainingAndFieldRole: trainingAndFieldRoleEnum.NONE,
    contentCreationRole: contentCreatingRoleEnum.NONE,
    administrationRole: workspaceUsersEnum.NONE,
    rolePresetId: null,
    presetName: null,
};

const STEP_HEADERS = {
    1: {icon: MailOutlineIcon, title: 'Add members', subtitle: 'Enter emails or upload a file'},
    2: {icon: PeopleOutlinedIcon, title: 'Set permissions', subtitle: 'Choose roles and groups'},
    3: {icon: SendOutlinedIcon, title: 'Final review', subtitle: 'Review and send invites'},
};

const InviteMembersModal = observer(() => {
    const {modalStore, generalStore, tableStore} = useStores();
    const [step, setStep] = useState(1);
    const [roles, setRoles] = useState(INITIAL_ROLES);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [accessPayload, setAccessPayload] = useState(null);
    const existingEmails = modalStore.modalData?.existingEmails || [];
    const emailHook = useEmailInput(existingEmails);

    const available = generalStore.availableSeats ?? 0;
    const total = generalStore.domainSettings?.MaxUsers ?? 0;
    const showCsvUpload = !!generalStore.domainSettings?.IsSupportAIChatWebEnabled;

    const permissions = {...roles, groups: selectedGroups};

    const handleSendSuccess = (inviteCount) => {
        if (generalStore.domainSettings) {
            generalStore.domainSettings.MembersCount += inviteCount;
        }
        tableStore.clearAllFilters();
        tableStore.sortColumn = 'InvitedOn';
        tableStore.sortDirection = sortingArrangeEnum.DESCENDING;
        tableStore.refresh();
    };

    const handleClose = () => {
        modalStore.isInviteMembersModalOpen = false;
        setStep(1);
        setRoles(INITIAL_ROLES);
        setSelectedGroups([]);
        setAccessPayload(null);
        emailHook.reset();
    };

    const hasUnsavedChanges = useCallback(() => {
        if (emailHook.emails.length > 0 || emailHook.inputValue.trim()) return true;
        if (selectedGroups.length > 0) return true;
        if (accessPayload) return true;
        if (roles.trainingAndFieldRole !== INITIAL_ROLES.trainingAndFieldRole) return true;
        if (roles.contentCreationRole !== INITIAL_ROLES.contentCreationRole) return true;
        if (roles.administrationRole !== INITIAL_ROLES.administrationRole) return true;
        if (roles.rolePresetId !== INITIAL_ROLES.rolePresetId) return true;
        return false;
    }, [emailHook.emails, emailHook.inputValue, selectedGroups, accessPayload, roles]);

    const attemptClose = useCallback(() => {
        if (!hasUnsavedChanges()) {
            handleClose();
            return;
        }
        modalStore.modalData = {
            ...modalStore.modalData,
            title: 'Discard changes?',
            bodyText: 'You have unsaved changes. Are you sure you want to discard them?',
            confirmText: 'Discard',
            confirmActions: handleClose,
            isDelete: true,
            isChangedColorButtons: false,
        };
        modalStore.isGeneralConfirmActionDialogOpen = true;
    }, [hasUnsavedChanges, handleClose, modalStore]);

    const {icon: HeaderIcon, title, subtitle} = STEP_HEADERS[step] || STEP_HEADERS[1];

    return (
        <Dialog
            open={modalStore.isInviteMembersModalOpen || false}
            onClose={attemptClose}
            maxWidth="sm"
            fullWidth
            className="dialog"
            PaperProps={{style: {borderRadius: 'var(--buttons-radius, 12px)'}}}
        >
            <div className="flex items-start justify-between px-6 pt-4 pb-0">
                <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-classic-button-normal text-classic-button-normal-text">
                        <HeaderIcon style={{fontSize: 22}} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-lg font-bold text-main-normal">{title}</span>
                        <span className="text-sm text-main-disabled">{subtitle}</span>
                    </div>
                </div>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon style={{fontSize: 20}} />
                </IconButton>
            </div>

            <div className="px-6 pt-4 pb-2">
                <StepIndicator currentStep={step} />
            </div>

            <DialogContent className="px-6 special-scroll">
                {step === 1 && (
                    <Step1Emails
                        emailHook={emailHook}
                        available={available}
                        total={total}
                        showCsvUpload={showCsvUpload}
                        onClose={handleClose}
                        onContinue={() => setStep(2)}
                    />
                )}
                {step === 2 && (
                    <Step2Permissions
                        roles={roles}
                        onRolesChange={setRoles}
                        selectedGroups={selectedGroups}
                        onGroupsChange={setSelectedGroups}
                        onBack={() => setStep(1)}
                        onClose={handleClose}
                        onContinue={() => setStep(3)}
                    />
                )}
                {step === 3 && (
                    <Step3FinalReview
                        emails={emailHook.validEmails}
                        permissions={permissions}
                        accessPayload={accessPayload}
                        onAccessChange={setAccessPayload}
                        onBack={() => setStep(2)}
                        onClose={handleClose}
                        onSendSuccess={handleSendSuccess}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
});

export default InviteMembersModal;
